<template>
  <div>
    <label
      :for="disabled ? undefined : id"
      class="mb-1 block"
      :class="contextClass"
    >
      {{ name }}
      <sup v-if="required" class="top-0 text-sm font-bold text-red-400">*</sup>
    </label>
    <div class="relative">
      <select
        :id="id"
        v-model.trim="model"
        :disabled="disabled"
        :class="[className, !model && 'text-gray-400']"
        class="block w-full cursor-pointer appearance-none rounded border border-solid border-gray-600 bg-gray-100 py-[5px] pl-2 pr-7 text-sm text-black focus:border-blue-400 [&>option:disabled]:text-gray-400 [&>option]:text-black"
        @change="handleChange"
      >
        <slot />
      </select>
      <IconCaret
        direction="down"
        class="pointer-events-none absolute right-1 top-1 h-6 w-6 text-gray-600"
        :class="disabled && 'opacity-40'"
      />
    </div>
    <span v-show="isError" class="mt-1 block px-2 text-xs text-red-400">
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
  import { v4 as uuidv4 } from 'uuid';

  interface IProps {
    id: string;
    name: string;
    required?: boolean;
    disabled?: boolean;
    isError?: boolean;
    errorMessage?: string;
    labelClass?: string;
  }

  const props = withDefaults(defineProps<IProps>(), {
    id: uuidv4(),
    name: '',
    required: false,
    disabled: false,
    isError: false,
    errorMessage: '',
    labelClass: '',
  });

  const emits = defineEmits<{
    (e: 'change', value: string | number): void;
  }>();

  const model = defineModel<string | number>({ default: '' });

  const className = computed((): string => {
    if (props.disabled) {
      return 'pointer-events-none cursor-not-allowed opacity-40';
    }
    if (props.isError) {
      return 'border-red-400';
    }
    return '';
  });

  const contextClass = computed((): string => {
    const defaultClass = 'mb-1 block text-xs text-gray-600';
    return props.labelClass ? props.labelClass : defaultClass;
  });

  const handleChange = (): void => {
    emits('change', model.value);
  };
</script>
